import { gql } from "@apollo/client";

export const CREATE_EVENT = gql`
  mutation CreateEvent($input: events_insert_input!) {
    insert_events_one(object: $input) {
      id
    }
  }
`;

export const UPDATE_EVENT = gql`
  mutation UpdateEvent($id: Int!, $input: events_set_input) {
    update_events_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
    }
  }
`;
// Merges the Merch Manager event settings into the event's `configuration`
// JSONB via `_append`, leaving other modules' keys (order_ahead_slug,
// checkout_field_label…) intact.
//
// Keys:
//   merch_payout_source   "festival" (debit the organizer's connected account)
//                         | "ronin" (transfer from the platform account) | null.
//                         Merch Manager FREEZES this onto the settlement row at
//                         lock time, so changes only affect later locks.
//   merch_tax_percentage  number AS ENTERED (7.5 = 7.5%). Merch Manager divides
//                         by 100 when stamping items — the items table stores a
//                         fraction (0.075).
//   merch_tax_type        "inclusive" | "exclusive".
//
// The tax keys are DEFAULTS applied to items created through Merch Manager;
// they never retro-edit existing items.
export const UPDATE_EVENT_MERCH_SETTINGS = gql`
  mutation UpdateEventMerchSettings($eventId: Int!, $patch: jsonb!) {
    update_events(
      where: { id: { _eq: $eventId } }
      _append: { configuration: $patch }
    ) {
      affected_rows
      returning {
        id
        configuration
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Merch Manager per-artist deals + fee lines (Inventory Configuration tab).
//
// These write tables admin-app otherwise never touches — see the matching block
// in ./queries.ts for what each one means. Two rules that are NOT enforced by
// GraphQL and will bite you if you skip them:
//
//   1. A LOCKED settlement (vendor_settlement.locked_at IS NOT NULL) freezes
//      both the split and the fee lines. The split upsert below has no lock
//      predicate, so the caller MUST check locked_at first. The delete does
//      carry the predicate, but Hasura reports a blocked delete as
//      affected_rows: 0, not an error — treat 0 as a failure.
//   2. flat splits have a DB CHECK that artist+organizer+manager = 100 exactly
//      (columns are numeric(5,2), so round each to 2dp BEFORE adding them up).
//      Violating it surfaces as a raw Postgres constraint name, not a message
//      anyone can read.
// ---------------------------------------------------------------------------

// Upsert the revenue split for one (event, artist) pair.
//
// split_mode 'flat'   — artist/organizer/manager are shares of adjusted gross
//                       and must total exactly 100.
// split_mode 'nested' — organizer_pct comes off net first, THEN manager_pct is
//                       taken OF THE REMAINDER, so the two may legitimately sum
//                       past 100. artist_pct is DERIVED, never typed:
//                         afterOrg = 1 - organizer/100
//                         artist   = round2(afterOrg * 100 - afterOrg * manager)
//                       Store that derived value — the settlement math reads the
//                       column, not the mode.
//
// manager_paid_in_app must always be false: paying vendor managers through the
// app isn't built, and Merch Manager's API rejects true. The manager's cut shows
// on the signed statement and settles outside the app.
export const UPSERT_MERCH_VENDOR_DEAL = gql`
  mutation UpsertMerchVendorDeal($object: merch_vendor_deal_insert_input!) {
    insert_merch_vendor_deal_one(
      object: $object
      on_conflict: {
        constraint: merch_vendor_deal_event_id_vendor_id_key
        update_columns: [
          split_mode
          artist_pct
          organizer_pct
          manager_pct
          manager_vendor_id
          manager_paid_in_app
          is_active
          updated_at
          last_updated_by
        ]
      }
    ) {
      id
    }
  }
`;

// Fee lines hang off vendor_settlement.id, which may not exist yet for a
// (event, vendor) pair — nothing creates settlements up front. Call this first
// and use the returned id. It is a no-op touch on an existing row, so it is
// safe to call repeatedly; the returned status/locked_at also re-confirm the
// lock state right before a write.
export const ENSURE_VENDOR_SETTLEMENT = gql`
  mutation EnsureVendorSettlement($object: vendor_settlement_insert_input!) {
    insert_vendor_settlement_one(
      object: $object
      on_conflict: {
        constraint: vendor_settlement_event_id_vendor_id_key
        update_columns: [updated_at]
      }
    ) {
      id
      status
      locked_at
    }
  }
`;

// One fee / deduction row. `rate` is a 0-100 percentage used when
// flat_or_pct = 'pct'; `amount_cents` is used when flat_or_pct = 'flat'.
// off_top = taken before the split, false = taken after it. kept_by is
// 'festival' for everything configured here.
export const INSERT_VENDOR_SETTLEMENT_LINE = gql`
  mutation InsertVendorSettlementLine(
    $object: vendor_settlement_line_insert_input!
  ) {
    insert_vendor_settlement_line_one(object: $object) {
      id
    }
  }
`;

// ---------------------------------------------------------------------------
// Creating merch participants from the event screen.
//
// Ops routinely find out an artist exists the morning of the event, so the
// Inventory Configuration tab can mint the vendor rows itself instead of sending
// them to /vendors/create and back.
// ---------------------------------------------------------------------------

// A vendor created from the merch tab.
//
// For an ARTIST: set NEITHER revenue_share NOR flat_fee. A null revenue_share
// is meaningful — it's what makes an artist with no merch_vendor_deal refuse to
// settle instead of quietly settling at 0%. VendorCreate defaults revenue_share
// to "0.00"; do not copy that here.
//
// For a VENDOR MANAGER: is_merch_vendor_manager = true (migration 008, nullable
// boolean). They still need a normal vendor `type` — that column is not
// nullable and the enum has no "manager" member.
export const CREATE_MERCH_VENDOR = gql`
  mutation CreateMerchVendor($object: vendors_insert_input!) {
    insert_vendors_one(object: $object) {
      id
      name
      type
    }
  }
`;

// Vendors are not event-scoped — THIS is what puts an artist on the event.
// GET_EVENT_MERCH_ARTISTS derives the list below from active locations, so a
// vendor without one of these is invisible on the Inventory Configuration tab no
// matter how correct its own row is.
export const CREATE_MERCH_LOCATION = gql`
  mutation CreateMerchLocation($object: locations_insert_input!) {
    insert_locations_one(object: $object) {
      id
    }
  }
`;

// The menu is where an artist's items live. Merch Manager's
// EnsureMenuForLocationAsync creates one lazily on first item, and REUSES an
// existing menu for the same location+vendor — so creating it up front here is
// safe and means the location is ready immediately. Keep this object identical to
// the C# one (name "<vendor> — Merch", cash + credit enabled) so the two paths
// can never produce subtly different menus.
export const CREATE_MERCH_MENU = gql`
  mutation CreateMerchMenu($object: menus_insert_input!) {
    insert_menus_one(object: $object) {
      id
    }
  }
`;

// menus <-> locations is a JOIN (location_menu, singular): one menu can serve
// several locations. This row is what makes the menu show up on the POS for that
// location.
export const CREATE_MERCH_LOCATION_MENU = gql`
  mutation CreateMerchLocationMenu($object: location_menu_insert_input!) {
    insert_location_menu_one(object: $object) {
      id
    }
  }
`;

// Detach ONE menu from ONE location, by the join row's own id.
//
// Deliberately keyed on location_menu.id rather than (location_id, menu_id):
// the pair is not unique in principle, and a where-clause on location_id alone
// (what modules/locations/mutations.ts does before rewriting the whole set)
// would take out every other module's menu on a shared location.
//
// Hasura reports a delete that matched nothing — including one blocked by
// permissions — as affected_rows: 0 rather than an error, so 0 is a failure.
export const DELETE_MERCH_LOCATION_MENU = gql`
  mutation DeleteMerchLocationMenu($id: Int!) {
    delete_location_menu(where: { id: { _eq: $id } }) {
      affected_rows
    }
  }
`;

// Edit a location in place — its name, or its location_type (a
// dashboard_reference value, reference_type 'locations_location_type', NOT a
// free string).
//
// This is NOT the way to remove one: `is_active` belongs to
// DEACTIVATE_MERCH_LOCATION below, which exists so that every caller clearing
// the flag goes past the same comment about what clearing it costs.
export const UPDATE_MERCH_LOCATION = gql`
  mutation UpdateMerchLocation($id: Int!, $input: locations_set_input!) {
    update_locations_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      name
      location_type
    }
  }
`;

// Remove a location — SOFT, always.
//
// locations rows are pointed at by orders, by menus (through location_menu) and
// by Merch Manager's physical counts (inventory_count_line.location_id), so a
// real DELETE either fails on a foreign key or takes history with it. Clearing
// is_active hides the location from every list in both apps while leaving all of
// that intact.
//
// Two things the caller owns, because GraphQL won't:
//   1. An active location is what puts a vendor on the Inventory Configuration tab
//      at all (GET_EVENT_MERCH_ARTISTS derives the artist list from locations).
//      Deactivating an artist's LAST one therefore removes the ARTIST from the
//      event's list — their deal and settlement rows survive, but they vanish
//      from the tab and from Merch Manager's vendor list until a location is
//      added back. Confirm that in those words before calling this.
//   2. Counts recorded at the location stay in the database but become unreachable
//      from the UI — see GET_MERCH_LOCATION_USAGE, which is how you find out
//      how many there are while the user can still say no.
//
// update_..._by_pk returns null — not an error — when the row is gone or a
// permission blocks the write, so a null payload is a FAILURE.
export const DEACTIVATE_MERCH_LOCATION = gql`
  mutation DeactivateMerchLocation($id: Int!, $userId: Int) {
    update_locations_by_pk(
      pk_columns: { id: $id }
      _set: { is_active: false, last_updated_by: $userId }
    ) {
      id
    }
  }
`;

// Remove an ARTIST from an event's merch configuration — SOFT, always, and in
// two steps because they hit two different tables.
//
// There is no events↔vendors join to unlink: an artist is on the tab because
// they own an ACTIVE LOCATION at the event (GET_EVENT_MERCH_ARTISTS derives the
// list from locations). So removing the artist = deactivating those locations.
// Deactivating the deal alone would leave them listed and silently drop them
// back to the legacy two-party split from vendors.revenue_share — a button that
// doesn't do what its label says.
//
// Deliberately scoped by (event_id, vendor_id) rather than by a list of ids the
// client happens to be holding: if someone added a location in another tab
// since this panel loaded, an id list would leave it behind and the artist
// would stay on the tab after a "successful" removal.
//
// Returns affected_rows. ZERO IS A FAILURE for the locations call — it means
// the rows were already gone or a permission blocked the write.
export const DEACTIVATE_MERCH_VENDOR_LOCATIONS = gql`
  mutation DeactivateMerchVendorLocations(
    $eventId: Int!
    $vendorId: Int!
    $userId: Int
  ) {
    update_locations(
      where: {
        event_id: { _eq: $eventId }
        vendor_id: { _eq: $vendorId }
        is_active: { _eq: true }
      }
      _set: { is_active: false, last_updated_by: $userId }
    ) {
      affected_rows
    }
  }
`;

// The deal half. Zero affected_rows here is NORMAL, not a failure: an artist on
// the legacy two-party model has no merch_vendor_deal row at all, and we must
// never insert a placeholder one (see the note on GET_EVENT_MERCH_DEALS).
//
// The row is kept and flagged rather than deleted so that re-adding a location
// later restores the artist WITH their previously agreed split, instead of
// silently resetting them to the festival default. UPSERT_MERCH_VENDOR_DEAL
// lists is_active in update_columns, so re-adding flips it straight back.
export const DEACTIVATE_MERCH_VENDOR_DEAL = gql`
  mutation DeactivateMerchVendorDeal(
    $eventId: Int!
    $vendorId: Int!
    $userId: Int
  ) {
    update_merch_vendor_deal(
      where: { event_id: { _eq: $eventId }, vendor_id: { _eq: $vendorId } }
      _set: { is_active: false, last_updated_by: $userId }
    ) {
      affected_rows
    }
  }
`;

// Hand an EXISTING location at this event to a different vendor.
//
// This is NOT a tagging operation. locations.vendor_id is the location's owner, and
// it is what puts a vendor on the Inventory Configuration tab at all — so this
// MOVES the location off whoever holds it now, and Merch Manager's counts
// (inventory_count_line rows, keyed on location_id) move with it. Callers must
// name BOTH vendors and get an explicit confirmation before calling this
// against a location that already has an owner.
//
// update_..._by_pk returns null — not an error — when the row doesn't exist or
// a permission blocks the update, so a null payload is a failure.
export const REASSIGN_MERCH_LOCATION = gql`
  mutation ReassignMerchLocation($id: Int!, $input: locations_set_input!) {
    update_locations_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      name
      vendor_id
    }
  }
`;

export const UPDATE_MERCH_MENU = gql`
  mutation UpdateMerchMenu($id: Int!, $input: menus_set_input!) {
    update_menus_by_pk(pk_columns: { id: $id }, _set: $input) {
      id
      name
    }
  }
`;

// Assign a vendor manager to this event (migration 008). UNIQUE(event_id,
// vendor_id), so re-assigning someone who was removed re-activates the existing
// row rather than erroring — that's what is_active is doing in update_columns.
export const UPSERT_MERCH_EVENT_MANAGER = gql`
  mutation UpsertMerchEventManager($object: merch_event_manager_insert_input!) {
    insert_merch_event_manager_one(
      object: $object
      on_conflict: {
        constraint: merch_event_manager_event_id_vendor_id_key
        update_columns: [is_active, updated_at, last_updated_by]
      }
    ) {
      id
      vendor_id
    }
  }
`;

// The locked_at predicate is the real guard — a locked settlement silently
// matches zero rows rather than erroring, so affected_rows must be checked.
export const DELETE_VENDOR_SETTLEMENT_LINE = gql`
  mutation DeleteVendorSettlementLine($id: Int!) {
    delete_vendor_settlement_line(
      where: { id: { _eq: $id }, settlement: { locked_at: { _is_null: true } } }
    ) {
      affected_rows
    }
  }
`;

export const DELETE_EVENT = gql`
  mutation DeleteEvent($id: Int!) {
    delete_events(where: { id: { _eq: $id } }) {
      returning {
        id
      }
    }
  }
`;
